import { useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Watch } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  DiscountChip,
  PriceRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { useSubscriptionPriceBreakdown } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/useSubscriptionPriceBreakdown";
import { useAlert } from "@/hooks/useAlert";
import { useSetAutoRenew } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { hasSimAutoRenewal, isWatchSimPausing } from "@/utils/deviceUtils";
import { getNextPaymentDate } from "@/utils/tariffUtils";
import { capitalizeFirstLetter, getCurrency } from "@/utils/translationHelpers";

export interface ChangeWatchSimRenewalProps {
  /**
   * Rendered inline (desktop) instead of as a full page (mobile) - mirrors
   * `SubscriptionAutoRenewal`. Omitted when mounted as the route's own
   * element (the mobile case).
   */
  onClose?: () => void;
}

export const ChangeWatchSimRenewal: FC<ChangeWatchSimRenewalProps> = ({
  onClose,
}) => {
  const { t: tPrefixed } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.watchSim.autoRenewal",
  });
  const { t, i18n } = useTranslation("portal");
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAlert = useAlert();

  const { data: simDetail, isLoading } = useDisplayedSim();

  const close = () =>
    onClose
      ? onClose()
      : navigate(`../display?simId=${simDetail?.simId}`, { replace: true });

  // Same source of truth as the status CTA — flagged cancellation, or an
  // ORDERED Watch whose successor has already dropped it.
  const isEnableScreen = !!simDetail && isWatchSimPausing(simDetail);
  const willEnableMainSimAutoRenewal =
    isEnableScreen && !!simDetail && !hasSimAutoRenewal(simDetail);
  const enableOrDisableKey = isEnableScreen ? "enable" : "disable";

  const { mutate: sendSetAutoRenew, isLoading: isLoadingSetAutoRenew } =
    useSetAutoRenew({
      mutation: {
        onSuccess: () =>
          // Same "stay pending until the panel actually reflects the new
          // state" ordering as SubscriptionAutoRenewal.
          queryClient.invalidateQueries(["/sim/sim"]).finally(() => {
            close();
            showAlert({
              type: AlertTypes.success,
              text: tPrefixed(`${enableOrDisableKey}.successMessage`),
            });
          }),
      },
    });

  const toggleWatchSimAR = () => {
    if (!simDetail) {
      return;
    }
    sendSetAutoRenew({
      simId: willEnableMainSimAutoRenewal
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          simDetail.simId!
        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          simDetail.watchSim!.watchId!,
      params: { autoRenew: isEnableScreen },
    });
  };

  const nextBillDate =
    simDetail?.remainingDays !== undefined
      ? getLocalizedDateString(
          getNextPaymentDate(simDetail.remainingDays),
          i18n.language,
        )
      : undefined;
  // Watch SIM is already listed as an add-on by this hook, so the enable
  // screen's breakdown falls out of it for free - see SubscriptionAutoRenewal.
  const priceBreakdown = useSubscriptionPriceBreakdown(
    isEnableScreen ? simDetail : undefined,
  );

  if (isLoading || !simDetail) {
    // Rendered inline (desktop) alongside the rest of the tab - a full-page
    // takeover would cover unrelated cards that are already visible. The
    // route-mounted (mobile, no onClose) case still gets the full screen.
    return onClose ? (
      <SubscriptionCard dataTestid="change-watch-sim-renewal" title="">
        <LoadingSpinner />
      </SubscriptionCard>
    ) : (
      <BrandLoadingScreen />
    );
  }

  return (
    <SubscriptionCard
      dataTestid="change-watch-sim-renewal"
      stickyHeader
      title={
        isEnableScreen
          ? tPrefixed("enable.cardTitle")
          : tPrefixed("disable.title")
      }
      action={
        <>
          <Button
            dataTestid="cancel-button"
            size="sm"
            variant="secondary"
            onClick={close}
          >
            {capitalizeFirstLetter(t("common.buttons.cancel"))}
          </Button>
          <Button
            dataTestid="success-button"
            size="sm"
            variant="primary"
            disabled={isLoadingSetAutoRenew}
            onClick={toggleWatchSimAR}
          >
            {capitalizeFirstLetter(t("common.buttons.confirm"))}
          </Button>
        </>
      }
    >
      {isEnableScreen ? (
        <>
          <p className="text-body-base font-semibold text-foreground-primary">
            {tPrefixed("enable.title")}
          </p>

          <p className="text-body-sm text-foreground-secondary">
            <Trans
              t={tPrefixed}
              values={{ count: simDetail.minContractDuration }}
            >
              enable.description
            </Trans>
          </p>

          <SubscriptionInfoBox>
            <SubscriptionInfoRow
              icon={<Watch className="size-6" />}
              title={t("common.device.attributes.watchSim")}
            />
          </SubscriptionInfoBox>

          <div className="flex w-full flex-col gap-2">
            <p className="text-body-sm font-semibold text-foreground-primary">
              {tPrefixed("enable.nextBillTitle")}
            </p>
            <p className="text-body-sm text-foreground-secondary">
              {tPrefixed("enable.nextBillDescription")}
            </p>
          </div>

          <div className="flex w-full flex-col">
            <PriceRow
              withBackdrop
              variant="strong"
              label={priceBreakdown.productName}
              value={getCurrency(priceBreakdown.basePrice, "CHF")}
            />

            {priceBreakdown.discounts.map(
              ({ id, amount, campaignPeriod, discountName }, index) => {
                const period = priceBreakdown.getDiscountPeriod(
                  campaignPeriod!,
                );

                return (
                  <PriceRow
                    key={id}
                    withDivider={
                      priceBreakdown.addOns.length === 0 &&
                      index === priceBreakdown.discounts.length - 1
                    }
                    label={
                      <>
                        {discountName}
                        {period && (
                          <>
                            <br />
                            {period}
                          </>
                        )}
                      </>
                    }
                    value={<DiscountChip amount={amount ?? 0} />}
                  />
                );
              },
            )}

            {priceBreakdown.addOns.map((addOn, index) => (
              <PriceRow
                key={addOn.label}
                withDivider={index === priceBreakdown.addOns.length - 1}
                label={addOn.label}
                value={getCurrency(addOn.price, "CHF")}
              />
            ))}

            <PriceRow
              variant="strong"
              // Without a known bill date, say nothing rather than guess -
              // same guard as SubscriptionAutoRenewal.
              label={
                nextBillDate
                  ? tPrefixed("enable.dueDate", { date: nextBillDate })
                  : ""
              }
              equalColumns
              value={
                <>
                  <span>{getCurrency(priceBreakdown.totalPrice, "CHF")}</span>
                  <span className="text-body-xs font-regular leading-md">
                    {t("cockpit.managementTile.tariff.payInterval", {
                      days: simDetail.minContractDuration ?? 30,
                    })}
                  </span>
                </>
              }
            />
          </div>

          <Notification
            title={tDefault("Content.goodToKnow")}
            description={tPrefixed(
              willEnableMainSimAutoRenewal
                ? "enable.goodToKnowText.withMainSimAR"
                : "enable.goodToKnowText.onlyWatchSimAR",
            )}
          />
        </>
      ) : (
        <>
          <p className="text-body-sm text-foreground-secondary">
            <Trans t={tPrefixed} values={{ count: simDetail.remainingDays }}>
              disable.description
            </Trans>
          </p>

          <SubscriptionInfoBox>
            <SubscriptionInfoRow
              icon={<Watch className="size-6" />}
              title={t("common.device.attributes.watchSim")}
            />
          </SubscriptionInfoBox>

          <Notification
            title={tDefault("Content.goodToKnow")}
            description={tPrefixed("disable.goodToKnowText")}
          />
        </>
      )}
    </SubscriptionCard>
  );
};
